/**
 * HeaderWrapper - Simple Wrapper Component
 *
 * This component wraps the MainMenu component from the Kohler DSM library,
 * passing navigation props for rendering the main site navigation.
 *
 * Props:
 * @param {Object} props - Component props
 * @param {Object} props.mainNavProps - Navigation data for MainMenu
 * @param {Object} props.phone - List of phone numbers to store in sessionStorage
 */
'use client';

import { useEffect, useState } from 'react';
import { MainMenu } from '@kohler-dsm-library/kohler-modules';
import {
  CAMPAIGN_PHONE_SESSION_KEY,
  DEFAULT_CAMPAIGN_PHONE_SESSION_KEY,
  readStoredCampaignPhone,
  readStoredDefaultCampaignPhone,
  writeSessionValue,
} from '@/utils/campaignPhoneStorage';

export default function HeaderClient({
  mainNavProps,
  promoProps,
  phone = '',
  matchedPhone = '',
  defaultPhone = '',
  allowTransparentNav = false,
}) {
  const [effectivePhone, setEffectivePhone] = useState(phone);

  useEffect(() => {
    const storedPhone = readStoredCampaignPhone();
    const storedDefaultPhone = readStoredDefaultCampaignPhone();
    const nextDefaultPhone = defaultPhone || storedDefaultPhone || '';

    if (nextDefaultPhone) {
      writeSessionValue(DEFAULT_CAMPAIGN_PHONE_SESSION_KEY, nextDefaultPhone);
    }

    const nextPhone = matchedPhone || storedPhone || phone || nextDefaultPhone || '';

    if (nextPhone) {
      writeSessionValue(CAMPAIGN_PHONE_SESSION_KEY, nextPhone);
    }

    setEffectivePhone(nextPhone);
  }, [defaultPhone, matchedPhone, phone]);

  const activePhone = matchedPhone || effectivePhone || phone;
  const baseNavigation =
    mainNavProps?.buttonCTA && !activePhone
      ? {
          ...mainNavProps,
          buttonCTA: {
            ...mainNavProps.buttonCTA,
            text: '',
            href: '',
            'aria-label': mainNavProps.buttonCTA.leftSection || 'Get a Free Quote',
          },
        }
      : mainNavProps;
  const mainNavigation = activePhone
    ? {
        ...mainNavProps,
        buttonCTA: {
          leftSection: mainNavProps?.buttonCTA?.leftSection || 'Get a Free Quote: ',
          ...(mainNavProps?.buttonCTA || {}),
          text: activePhone.replace(/(\d{3})-(\d{3})-(\d{4})/, '($1) $2-$3'),
          'aria-label': `Get a Free Quote: ${activePhone}`,
          href: activePhone.replace(/(\d{3})-(\d{3})-(\d{4})/, '($1) $2-$3'),
        },
      }
    : baseNavigation;

  useEffect(() => {
    const headerElement = document.querySelector('header.kohler-main-menu-root');

    if (allowTransparentNav) {
      headerElement?.style.removeProperty('--main-menu-height');

      if (headerElement) {
        headerElement.style.minHeight = '';
      }

      return undefined;
    }

    const syncHeaderHeight = () => {
      const headerElement = document.querySelector('header.kohler-main-menu-root');
      const navigationBar = headerElement?.querySelector('.kohler-main-navigation-bar-root');
      const promoBanner = headerElement?.querySelector('.kohler-message-banner-root');

      if (!headerElement || !navigationBar) {
        return;
      }

      const navigationHeight = Math.ceil(navigationBar.getBoundingClientRect().height);
      const promoBarHeight = promoBanner
        ? Math.ceil(promoBanner.getBoundingClientRect().height)
        : 0;

      if (!navigationHeight) {
        return;
      }
      const mainMenuHeight = navigationHeight + promoBarHeight;
      headerElement.style.setProperty('--main-menu-height', `${mainMenuHeight}px`);
      headerElement.style.minHeight = `${mainMenuHeight}px`;
    };

    syncHeaderHeight();
    const rafId = window.requestAnimationFrame(syncHeaderHeight);
    const timeoutId = window.setTimeout(syncHeaderHeight, 250);
    window.addEventListener('resize', syncHeaderHeight);

    const navigationBar = headerElement?.querySelector('.kohler-main-navigation-bar-root');
    const resizeObserver =
      navigationBar && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => syncHeaderHeight())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(navigationBar);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', syncHeaderHeight);
      resizeObserver?.disconnect();
    };
  }, [allowTransparentNav]);

  return <MainMenu mainNavigation={mainNavigation} promoBanner={promoProps} />;
}
